'use client';

import { type CSSProperties, type MouseEvent, type ReactNode } from 'react';

import { ArrowLeft } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Creator bio home — where Back lands. */
  bioHref: string;
  creatorName: string;
  /** Creator backdrop (image / gradient / color) painted behind the centered card. */
  pageBackground: CSSProperties;
}

/**
 * Standalone post-detail page shell — the hard-nav / deep-link / refresh target
 * for `/<urlCode>/post/<year>/<month>/<slug>` (the intercept modal is the soft-nav
 * overlay; this is the focus view). Centers the (server-rendered) `PostDetailView`
 * card on the creator's backdrop, full-screen on mobile, and overlays the Back
 * controls. Back leaves via a hard `location.replace` so a subsequent browser
 * Back skips this route — the terminal-nav fix for cacheComponents' `<Activity>`
 * (same as the clip page).
 */
export function PostDetailStandalone({
  children,
  bioHref,
  creatorName,
  pageBackground,
}: Props) {
  const leaveToBio = (event: MouseEvent) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    window.location.replace(bioHref);
  };

  return (
    <div className="relative min-h-dvh w-full lg:flex lg:items-center lg:justify-center lg:p-6">
      <div className="fixed inset-0 -z-10" style={pageBackground} aria-hidden />

      {/* Mobile back — pinned to the viewport so it stays while the page scrolls. */}
      <a
        href={bioHref}
        onClick={leaveToBio}
        aria-label={`Back to ${creatorName}`}
        className="fixed left-2 top-1.5 z-20 flex h-9 w-9 items-center justify-center rounded-full text-black/70 transition hover:bg-black/5 lg:hidden"
      >
        <ArrowLeft className="h-5 w-5" aria-hidden />
      </a>

      {/* Desktop back link — aligned above the card, like the clip page. */}
      <a
        href={bioHref}
        onClick={leaveToBio}
        className="absolute left-4 top-4 z-20 hidden items-center gap-2 rounded-full bg-black/40 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-black/60 lg:flex"
      >
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        <span className="max-w-[16rem] truncate">{creatorName}</span>
      </a>

      {children}
    </div>
  );
}
